import {observable, computed, autorun, action, reaction, toJS, untracked, runInAction} from 'mobx';
import moment from 'moment';

import {TIME_UNIT_DURATION} from '../model/const';

import {selfGuard, noUndef, noDiff} from '../utils';
import {roundTime, ceilTime, floorTime} from '../utils';


const VIEW_TYPE ={
  LIST_NOMAP: 0,
  TILES_NOMAP: 1,
  LIST_MAP: 2,
  TILES_MAP: 3,
};

const VIEW_TYPE_CONTAINER_CLASS = {
  [VIEW_TYPE.LIST_NOMAP]: "nomap list",
  [VIEW_TYPE.TILES_NOMAP]: "nomap tiles",
  [VIEW_TYPE.LIST_MAP]: "map list",
  [VIEW_TYPE.TILES_MAP]: "map tiles",
}

export class VSearch {
  @observable viewType = VIEW_TYPE.TILES_NOMAP;
  @observable pageSize = 9;
  @observable pageNumber = 1;
  @observable canLoadMore = true;
  @observable searchInProgress = false;
 
  @observable locations = [];

  savedScrollPosition;

  @action.bound handleScroll(scrollY) {
    this.savedScrollPosition = scrollY;
  }

  @computed get searchResultItems() {
    return this.searchResults.items;
  }

  constructor(flasher, api, searchForm, searchResults, masterData, organization, businessUnit) {
    this.flasher = flasher;
    this.api = api;
    this.searchForm = searchForm;
    this.searchResults = searchResults;
    this.masterData = masterData;
    this.organization = organization;
    this.businessUnit = businessUnit;

    this.reactions = [];
  }

  @action.bound changeView() {
    const hasMoreLocations = this.searchForm.optLocation.length > 1;
    if(hasMoreLocations) {
      switch(this.viewType) {
        case VIEW_TYPE.LIST_NOMAP:
          this.viewType = VIEW_TYPE.TILES_NOMAP;
          break;
        case VIEW_TYPE.TILES_NOMAP:
          this.viewType = VIEW_TYPE.LIST_MAP;
          break;
        case VIEW_TYPE.LIST_MAP:
          this.viewType = VIEW_TYPE.TILES_MAP;
          break;
        case VIEW_TYPE.TILES_MAP:
          this.viewType = VIEW_TYPE.LIST_NOMAP;
          break;
      }
    } else {
      switch(this.viewType) {
        case VIEW_TYPE.LIST_NOMAP:
          this.viewType = VIEW_TYPE.TILES_NOMAP;
          break;
        case VIEW_TYPE.TILES_NOMAP:
          this.viewType = VIEW_TYPE.LIST_NOMAP;
          break;
      }      
    }
  }

  @action resetView() {
    if(this.searchForm.optLocation.length > 1) {
      this.viewType = VIEW_TYPE.TILES_MAP;
    }
  }

  @computed get viewTypeClass() {
    return VIEW_TYPE_CONTAINER_CLASS[this.viewType];
  }

  @computed get showsMap() {
    return this.viewType === VIEW_TYPE.LIST_MAP || this.viewType === VIEW_TYPE.TILES_MAP;
  }

  async startOrUpdate({orgId, location, category, subcategory, startDate, duration, timeUnitId, endDate}, router) {
    this.orgId = orgId;
    await Promise.all([
      this.masterData.cFetch(),
      this.organization.cFetch({orgId}),
    ]);

    let orgTimeAtomSecs = TIME_UNIT_DURATION[this.organization.refAllotmentTimeUnitId];
    if(!orgTimeAtomSecs) {
      console.error('No length for this organization atom.');
      orgTimeAtomSecs = 15*60;
    }

    await this.searchForm.startOrUpdate({orgId, orgTimeAtomSecs});

    if(!startDate) {
      startDate = roundTime(moment(), orgTimeAtomSecs);
      router.setParams({searchStartDate: startDate});
    }     

    startDate = floorTime(startDate, orgTimeAtomSecs);
    endDate = ceilTime(endDate, orgTimeAtomSecs);    

    // TODO: This is going to be copy pasted to VSearch - REFACTOR.
    runInAction(() => {
      this.searchForm.reset({ 
        location,
        category,
        subcategory,
        startDate,
        duration,
        timeUnit: timeUnitId,
        endDate,
      })

      if(!this.searchForm.startDate) {
        this.searchForm.changeStartDate(startDate);
      }
      if(!this.searchForm.duration) {
        this.searchForm.changeDuration(1);
        router.setParams({searchDuration: this.searchForm.duration});
      }
      if(!this.searchForm.timeUnit) {
        if(this.organization.timeUnits.length > 0) {
          const tuId = this.organization.timeUnits[0].id;
          this.searchForm.changeTimeUnit(tuId);
        }
        router.setParams({searchTimeUnitId: this.searchForm.timeUnit});
      }
    })

    this.stopReactions();

    this.reactions.push(
      reaction(
        () => {
          const {location, category, subcategory, startDate, endDate, timeUnit, duration} = this.searchForm;
          return {location, category, subcategory, startDate, endDate, timeUnit, duration};
        },
        ({location, category, subcategory, startDate, endDate, timeUnit, duration}) => {
          this.searchResults.setItems([]);
          router.setParams({
            location,
            category,
            subcategory,
            searchStartDate: startDate,
            searchEndDate: endDate,
            searchTimeUnitId: timeUnit,
            searchDuration: duration,
          })
        },
        {
          compareStructural: true,
          fireImmediately: true,
        }           
      )
    )

    
    this.reactions.push(
      reaction(
        () => {
          const {location, category, subcategory, endDate} = this.searchForm;
          return {location, category, subcategory, endDate};
        },
        async (input) => {
          const {location, category, subcategory, endDate} = input;
          const startDate = untracked(() => this.searchForm.startDate);
          this.searchResults.setItems([]);
          try {
            this.flasher.setOther('Loading.');
            this.resetView();
            if(startDate && endDate) {
              this.pageNumber = 1;
              this.canLoadMore = false;
              this.savedScrollPosition = undefined;
              
              let items;
              try {
                this.searchInProgress = true;
                items = await this.searchResults.get({
                  categoryId: category, 
                  organizationId: orgId, 
                  subcategoryId: subcategory, 
                  businessUnitId: location, 
                  startDate: startDate, 
                  endDate: endDate,
                  pageSize: untracked(() => this.pageSize + 1),
                  pageNumber: untracked(() => this.pageNumber),
                })
              }
              finally {
                this.searchInProgress = false;
              }
              this.searchResults.setItems(items);
              if(items.length >= this.pageSize + 1) {
                this.canLoadMore = true;
              }
            }
          }
          finally {
            this.flasher.setOther();
          }
        },
        {
          compareStructural: true,
          fireImmediately: this.searchResults.items.length === 0,
        }
      )
    );
    if(this.savedScrollPosition) {
      window.scrollTo(0, this.savedScrollPosition);
    }   
  }

  @action.bound async loadMore() {
    const {location, category, subcategory, startDate, endDate} = this.searchForm;
    const orgId = this.orgId
    if(startDate && endDate) {
      this.pageNumber++;
      this.canLoadMore = false;
      let items;
      try {
        this.searchInProgress = true;
        items = await this.searchResults.get({
          categoryId: category, 
          organizationId: orgId, 
          subcategoryId: subcategory, 
          businessUnitId: location, 
          startDate: startDate, 
          endDate: endDate,
          pageSize: this.pageSize + 1,
          pageNumber: this.pageNumber,
        });
      }
      finally {
        this.searchInProgress = false;
      }
      if(items.length >= this.pageSize + 1) {
        this.canLoadMore = true;
      }
      this.searchResults.addItems(items);
    }

  }

  slicedSearchResults(searchResults) {
    if(this.canLoadMore || this.searchInProgress) {
      let seeOnlyCnt = Math.floor(searchResults.length / this.pageSize) * this.pageSize;
      return searchResults.slice(0, seeOnlyCnt);
    } else {
      return searchResults;
    }
  }

  @action.bound stop() {
    this.searchForm.stop();
    this.stopReactions();
  }

  @action.bound stopReactions() {
    this.reactions.forEach(ar => ar());
    this.reactions = [];
  }  

} 


// WEBPACK FOOTER //
// ./src/view/VSearch.js